import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { Manufacturer } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminManufacturerGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<Manufacturer>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminManufacturerGetParams {
  limit: number;
  offset: number;
  sort: string;
  search: string;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  search: 'string',
}

export const useManufacturerListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminManufacturerGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<Manufacturer>>(
    [Entities.Manufacturers, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
        search,
      } = queryParams
      const { data } = await api.v1AdminManufacturersGet(
        limit,
        offset,
        sort,
        undefined,
        search,
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
